/**
 * Firestore CRUD for tabular cell / row / column highlights on CSV viewer (csvCellHighlights collection).
 * Mirrors the dual-key (documentId + documentUrl) subscribe pattern used by documentMarkupService and
 * pdfAnnotationService so legacy uploads keyed by URL still resolve.
 */

import { db } from './firebase';
import { collection, addDoc, query, where, onSnapshot, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import type { CsvCellHighlight, CsvHighlightScope } from '../types';

const COLLECTION = 'csvCellHighlights';

function fromFirestore(snap: { id: string; data: () => Record<string, unknown> }): CsvCellHighlight {
  const d = snap.data();
  return {
    id: snap.id,
    documentId: d.documentId as string,
    documentUrl: d.documentUrl as string,
    leadId: d.leadId as string,
    projectId: (d.projectId as string) || undefined,
    scope: (d.scope as CsvHighlightScope) || 'cell',
    row: typeof d.row === 'number' ? (d.row as number) : undefined,
    col: typeof d.col === 'number' ? (d.col as number) : undefined,
    colKey: (d.colKey as string) || undefined,
    color: (d.color as string) || '#fde68a',
    userId: d.userId as string,
    userName: d.userName as string,
    createdAt: (d.createdAt as { toDate: () => Date })?.toDate?.() ?? new Date(),
  };
}

export async function createCellHighlight(highlight: Omit<CsvCellHighlight, 'id'>): Promise<string> {
  const docData: Record<string, unknown> = {
    documentId: highlight.documentId,
    documentUrl: highlight.documentUrl,
    leadId: highlight.leadId,
    scope: highlight.scope,
    color: highlight.color,
    userId: highlight.userId,
    userName: highlight.userName,
    createdAt: Timestamp.fromDate(highlight.createdAt),
  };
  if (highlight.projectId) docData.projectId = highlight.projectId;
  if (typeof highlight.row === 'number') docData.row = highlight.row;
  if (typeof highlight.col === 'number') docData.col = highlight.col;
  if (highlight.colKey) docData.colKey = highlight.colKey;
  const ref = await addDoc(collection(db, COLLECTION), docData);
  return ref.id;
}

export async function deleteCellHighlight(highlightId: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, highlightId));
}

/** Subscribe to highlights for a CSV document (merged by documentId and optionally documentUrl). */
export function subscribeCellHighlightsForDocument(
  documentId: string,
  documentUrl: string,
  callback: (highlights: CsvCellHighlight[]) => void,
): () => void {
  const unsubs: (() => void)[] = [];
  let byId: CsvCellHighlight[] = [];
  let byUrl: CsvCellHighlight[] = [];
  const mergeAndSort = () => {
    const seen = new Set<string>();
    const merged: CsvCellHighlight[] = [];
    for (const h of [...byId, ...byUrl]) {
      if (h.id && !seen.has(h.id)) {
        seen.add(h.id);
        merged.push(h);
      } else if (!h.id) merged.push(h);
    }
    callback(merged.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime()));
  };
  unsubs.push(
    onSnapshot(query(collection(db, COLLECTION), where('documentId', '==', documentId)), snapshot => {
      byId = snapshot.docs.map(d => fromFirestore({ id: d.id, data: () => d.data() }));
      mergeAndSort();
    }),
  );
  if (documentUrl && documentUrl !== documentId) {
    unsubs.push(
      onSnapshot(query(collection(db, COLLECTION), where('documentUrl', '==', documentUrl)), snapshot => {
        byUrl = snapshot.docs.map(d => fromFirestore({ id: d.id, data: () => d.data() }));
        mergeAndSort();
      }),
    );
  }
  return () => unsubs.forEach(u => u());
}
